import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { LogOut, User, Heart, Bell, Menu, X } from "lucide-react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const loc = useLocation();
    const isActive = (p) => loc.pathname === p;
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!user || user.role !== "user") return;
        const fetchN = () => {
            api.get("/me/notifications")
                .then((r) => setUnread(r.data?.unread_count || 0))
                .catch(() => {});
        };
        fetchN();
        const t = setInterval(fetchN, 60000);
        return () => clearInterval(t);
    }, [user]);

    useEffect(() => {
        if (loc.pathname === "/following" && user?.role === "user" && unread > 0) {
            api.post("/me/notifications/mark-read").then(() => setUnread(0)).catch(() => {});
        }
    }, [loc.pathname, user, unread]);

    // Close the mobile drawer when the route changes
    useEffect(() => { setOpen(false); }, [loc.pathname]);

    // Lock body scroll while the mobile drawer is open
    useEffect(() => {
        if (open) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const doLogout = async () => { await logout(); setOpen(false); navigate("/"); };

    const NavLinks = ({ mobile = false, onNav }) => {
        const cls = (active) =>
            mobile
                ? `block py-3 border-b border-white/10 font-display font-bold text-2xl tracking-tight ${active ? "text-brand-saffron" : "text-white hover:text-brand-saffron"}`
                : `hover:text-brand-saffron transition-colors ${active ? "text-brand-saffron" : ""}`;
        return (
            <>
                <Link to="/" data-testid={mobile ? "mobile-nav-home" : "nav-home"} onClick={onNav} className={cls(isActive('/'))}>Home</Link>
                <Link to="/discover" data-testid={mobile ? "mobile-nav-discover" : "nav-discover"} onClick={onNav} className={cls(isActive('/discover'))}>Discover</Link>
                <Link to="/drops" data-testid={mobile ? "mobile-nav-drops" : "nav-drops"} onClick={onNav} className={cls(isActive('/drops'))}>Drops</Link>
                {user && user.role === "user" && (
                    <Link to="/following" data-testid={mobile ? "mobile-nav-following" : "nav-following"} onClick={onNav} className={`${cls(isActive('/following'))} ${mobile ? "flex items-center gap-2" : "flex items-center gap-1"}`}>
                        <Heart className={mobile ? "w-4 h-4" : "w-3 h-3"} /> Following {mobile && unread > 0 && <span className="ml-auto px-2 py-0.5 rounded-full bg-brand-magenta text-white text-xs">{unread}</span>}
                    </Link>
                )}
                {user && user.role === "user" && (
                    <Link to="/profile" data-testid={mobile ? "mobile-nav-profile" : "nav-profile"} onClick={onNav} className={cls(isActive('/profile'))}>Profile</Link>
                )}
                {user && user.role === "startup" && (
                    <Link to="/dashboard" data-testid={mobile ? "mobile-nav-dashboard" : "nav-dashboard"} onClick={onNav} className={cls(isActive('/dashboard'))}>Dashboard</Link>
                )}
                {user && user.role === "admin" && (
                    <Link to="/admin" data-testid={mobile ? "mobile-nav-admin" : "nav-admin"} onClick={onNav} className={cls(isActive('/admin'))}>Admin</Link>
                )}
            </>
        );
    };

    return (
        <>
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
            <nav className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-10 h-16">
                <Link to="/" data-testid="nav-logo" className="font-display font-extrabold text-xl md:text-2xl tracking-tighter">
                    PRIMATES<span className="text-brand-saffron">.</span>
                </Link>

                {/* Desktop links */}
                <div className="hidden md:flex items-center gap-7 font-mono-tag text-xs uppercase">
                    <NavLinks />
                </div>

                {/* Right-side controls */}
                <div className="flex items-center gap-2 md:gap-3">
                    {user?.role === "user" && (
                        <Link to="/following" data-testid="nav-notifications-bell" className="relative p-2 rounded-full hover:bg-white/10 transition-colors">
                            <Bell className="w-4 h-4 text-white/80" />
                            {unread > 0 && (
                                <span data-testid="nav-notifications-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-brand-magenta text-white text-[10px] font-bold flex items-center justify-center">
                                    {unread > 9 ? "9+" : unread}
                                </span>
                            )}
                        </Link>
                    )}

                    {/* Desktop-only auth buttons */}
                    <div className="hidden md:flex items-center gap-3">
                        {user ? (
                            <>
                                <span className="font-mono-tag text-xs text-white/60"><User className="inline w-3 h-3 mr-1" />{user.name}</span>
                                <Button data-testid="nav-logout-btn" onClick={doLogout} variant="ghost" size="sm" className="text-white hover:bg-white/10">
                                    <LogOut className="w-4 h-4 mr-1" />Logout
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button data-testid="nav-login-btn" onClick={() => navigate("/login")} variant="ghost" size="sm" className="text-white hover:bg-white/10">Login</Button>
                                <Button data-testid="nav-signup-btn" onClick={() => navigate("/register")} size="sm" className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold">Sign up</Button>
                            </>
                        )}
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden p-2.5 rounded-md border border-white/15 hover:border-brand-saffron transition-colors"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </nav>
        </header>

        {/* Mobile drawer — rendered OUTSIDE the backdrop-blur header so its stacking context is not clipped */}
        {open && (
            <div data-testid="mobile-menu-drawer" className="md:hidden fixed inset-x-0 top-16 bottom-0 z-[60] bg-black/95 backdrop-blur-xl overflow-y-auto">
                <div className="px-6 py-6 flex flex-col">
                    <p className="font-mono-tag text-[10px] uppercase text-white/40 mb-3">/ navigate</p>
                    <NavLinks mobile onNav={() => setOpen(false)} />

                    <p className="font-mono-tag text-[10px] uppercase text-white/40 mt-8 mb-3">/ account</p>
                    {user ? (
                        <>
                            <p className="text-white/70 text-sm mb-4"><User className="inline w-4 h-4 mr-2" />{user.name} <span className="ml-2 text-xs text-white/40 uppercase">({user.role})</span></p>
                            <Button data-testid="mobile-nav-logout-btn" onClick={doLogout}
                                variant="outline" className="w-full h-12 border-white/20 text-white bg-transparent hover:bg-white/10 rounded-md">
                                <LogOut className="w-4 h-4 mr-2" />Logout
                            </Button>
                        </>
                    ) : (
                        <div className="flex flex-col gap-3">
                            <Button data-testid="mobile-nav-login-btn" onClick={() => { navigate("/login"); setOpen(false); }}
                                variant="outline" className="w-full h-12 border-white/20 text-white bg-transparent hover:bg-white/10 rounded-md">
                                Login
                            </Button>
                            <Button data-testid="mobile-nav-signup-btn" onClick={() => { navigate("/register"); setOpen(false); }}
                                className="w-full h-12 bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold rounded-md">
                                Sign up
                            </Button>
                        </div>
                    )}

                    <p className="font-mono-tag text-[10px] uppercase text-white/40 mt-10 mb-3">/ legal</p>
                    <Link to="/privacy" onClick={() => setOpen(false)} className="text-white/60 text-sm py-1">Privacy Policy</Link>
                    <Link to="/terms" onClick={() => setOpen(false)} className="text-white/60 text-sm py-1">Terms & Conditions</Link>
                </div>
            </div>
        )}
        </>
    );
}
